import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';

const BarChart = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Função para calcular se deve usar texto branco ou preto baseado na luminosidade da cor
  const getContrastColor = (hexColor) => {
    // Remove o # se existir
    const hex = hexColor.replace('#', '');
    
    // Converte hex para RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calcula a luminosidade usando a fórmula padrão
    // Valores RGB são normalizados para 0-1 e aplicada correção gamma
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Se luminosidade > 0.5, cor é clara (usar texto preto)
    // Se luminosidade <= 0.5, cor é escura (usar texto branco)
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // Transformar os dados para o formato que o Nivo espera
  const yearsToShow = Array.from({ length: 10 }, (_, i) => i);
  const nivoData = yearsToShow.map(yearIndex => {
    const item = data[yearIndex];
    if (!item) return null;
    
    return {
      year: `Ano ${item.year}`,
      'Modelo Tradicional': item.traditional,
      'Fee Fixo Monefica': item.monefica,
      'Economia': item.traditional - item.monefica
    };
  }).filter(Boolean);

  const formatValue = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return 'R$ 0';
    return `R$ ${value.toLocaleString('pt-BR')}`;
  };

  // Cores das barras
  const barColors = ['#FDA506', '#121C42'];

  // Configurações responsivas
  const chartHeight = isMobile ? 250 : 350;
  const margins = isMobile 
    ? { top: 40, right: 10, bottom: 60, left: 40 }
    : { top: 10, right: 60, bottom: 70, left: 80 };
  
  const titleVariant = isMobile ? 'h6' : 'h4';
  
  // Função customizada para renderizar labels em mobile
  const customLabel = (d) => {
    const value = Number(d.value) || 0;
    if (value === 0) return '';
    
    if (isMobile) {
      // Para mobile, usar formato mais compacto
      if (value < 1000) {
        return `${value}`;
      } else if (value < 1000000) {
        return `${Math.round(value/1000)}k`;
      } else {
        return `${(value/1000000).toFixed(1)}M`;
      }
    } else {
      // Para desktop, formato padrão
      if (value < 1000) {
        return `${value}`;
      } else if (value < 1000000) {
        return `${Math.round(value/1000)}k`;
      } else {
        return `${(value/1000000).toFixed(1)}M`;
      }
    }
  };
  
  const legendConfig = isMobile 
    ? {
        dataFrom: 'keys',
        anchor: 'top-left',
        direction: 'column',
        justify: false,
        translateX: 0,
        translateY: 5,
        itemsSpacing: 20,
        itemWidth: 120,
        itemHeight: 14,
        itemDirection: 'left-to-right',
        effects: [
          {
            on: 'hover',
            style: {
              itemOpacity: 1
            }
          }
        ]
      }
    : {
        dataFrom: 'keys',
        anchor: 'top-left',
        direction: 'column',
        justify: false,
        translateX: 15,
        translateY: 4,
        itemsSpacing: 3,
        itemWidth: 100,
        itemHeight: 16,
        effects: [
          {
            on: 'hover',
            style: {
              itemOpacity: 1
            }
          }
        ]
      };

  return (
    <Box sx={{ width: '100%', backgroundColor: 'white' }}>
      <Typography variant={titleVariant} sx={{ mb: 2, color: '#333', fontSize: isMobile ? '1.1rem' : undefined }}>
        Diferença de custos entre fee fixo e comissão por produto, acumulada ao longo de 10 anos
      </Typography>
      <Box sx={{ width: '100%', height: chartHeight }}>
        <ResponsiveBar
          data={nivoData}
          keys={['Modelo Tradicional', 'Fee Fixo Monefica']}
          indexBy="year"
          margin={margins}
          padding={isMobile ? 0.1 : 0.3}
          groupMode="grouped"
          enableGridY={false}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={barColors}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 1.6]]
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: isMobile ? -45 : 0,
            legend: 'Período (em anos)',
            legendPosition: 'middle',
            legendOffset: isMobile ? 45 : 50,
            tickTextColor: '#333'
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 0,
            tickRotation: 0,
            legend: 'Valores (R$)',
            legendPosition: 'middle',
            legendOffset: isMobile ? -35 : -60,
            format: () => '',
            tickTextColor: 'transparent'
          }}
          enableLabel={false}
          labelSkipWidth={isMobile ? 4 : 12}
          labelSkipHeight={isMobile ? 4 : 12}
          labelTextColor={({ color }) => getContrastColor(color)}
          theme={{
            labels: {
              text: {
                fontSize: isMobile ? 10 : 11,
                fontWeight: 600,
                fill: ({ color }) => getContrastColor(color)
              }
            },
            axis: {
              ticks: {
                text: {
                  fontSize: isMobile ? 10 : 12,
                  fill: '#333333'
                }
              },
              legend: {
                text: {
                  fontSize: isMobile ? 10 : 12,
                  fill: '#333333',
                  fontWeight: 500
                }
              }
            },
            grid: {
              line: {
                stroke: 'rgba(0,0,0,0.1)',
                strokeWidth: 1,
                strokeDasharray: '4 4'
              }
            },
            legends: {
              text: {
                fontSize: isMobile ? 9 : 11,
                fill: '#333333'
              }
            }
          }}
          // Função customizada para renderizar labels verticais em mobile
          layers={[
            'grid',
            'axes',
            'bars',
            // Layer customizada para labels
            ({ bars, ...props }) => {
              // Encontrar a menor barra (menor altura)
              const smallestBar = bars.reduce((smallest, current) => {
                if (!smallest) return current;
                return current.height < smallest.height ? current : smallest;
              }, null);

              if (!isMobile) {
                return (
                  <g>
                    {bars.map(bar => {
                      const value = customLabel(bar.data);
                      if (!value || value === '0') return null;
                      
                      // Verificar se esta é a menor barra
                      const isSmallestBar = smallestBar && bar.key === smallestBar.key;
                      
                      if (isSmallestBar) {
                        // Posicionar número acima da menor barra com cor preta
                        return (
                          <text
                            key={bar.key}
                            x={bar.x + bar.width / 2}
                            y={bar.y - 15}
                            textAnchor="middle"
                            dominantBaseline="central"
                            transform={isMobile ? `rotate(-90, ${bar.x + bar.width / 2}, ${bar.y - 15})` : undefined}
                            style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              fill: '#000000'
                            }}
                          >
                            {value}
                          </text>
                        );
                      }
                      
                      return (
                        <text
                          key={bar.key}
                          x={bar.x + bar.width / 2}
                          y={bar.y + bar.height / 2}
                          textAnchor="middle"
                          dominantBaseline="central"
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            fill: getContrastColor(bar.color)
                          }}
                        >
                          {value}
                        </text>
                      );
                    })}
                  </g>
                );
              }
              
              // Para mobile, renderizar texto vertical
              return (
                <g>
                  {bars.map(bar => {
                    const value = customLabel(bar.data);
                    if (!value || value === '0') return null;
                    
                    // Verificar se esta é a menor barra
                    const isSmallestBar = smallestBar && bar.key === smallestBar.key;
                    
                    if (isSmallestBar) {
                      // Posicionar número acima da menor barra com cor preta
                      return (
                        <text
                          key={bar.key}
                          x={bar.x + bar.width / 2}
                          y={bar.y - 15}
                          textAnchor="middle"
                          dominantBaseline="central"
                          transform={`rotate(-90, ${bar.x + bar.width / 2}, ${bar.y - 15})`}
                          style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            fill: '#000000'
                          }}
                        >
                          {value}
                        </text>
                      );
                    }
                    
                    // Verificar se o valor cabe horizontalmente
                    const barWidth = bar.width;
                    const textWidth = value.length * 6; // Aproximação
                    const useVertical = textWidth > barWidth - 4;
                    
                    // Verificar se o valor cabe verticalmente quando rotacionado
                    const textHeight = value.length * 6; // Aproximação da altura quando vertical
                    const fitsVertically = textHeight <= bar.height - 4;
                    
                    // Se não cabe nem horizontal nem vertical, colocar acima da barra
                    if (useVertical && !fitsVertically) {
                      return (
                        <text
                          key={bar.key}
                          x={bar.x + bar.width / 2}
                          y={bar.y - 15}
                          textAnchor="middle"
                          dominantBaseline="central"
                          transform={`rotate(-90, ${bar.x + bar.width / 2}, ${bar.y - 15})`}
                          style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            fill: '#000000'
                          }}
                        >
                          {value}
                        </text>
                      );
                    }
                    
                    return (
                      <text
                        key={bar.key}
                        x={bar.x + bar.width / 2}
                        y={bar.y + bar.height / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        transform={useVertical ? `rotate(-90, ${bar.x + bar.width / 2}, ${bar.y + bar.height / 2})` : undefined}
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          fill: getContrastColor(bar.color)
                        }}
                      >
                        {value}
                      </text>
                    );
                  })}
                </g>
              );
            },
            'legends'
          ]}
          gridYValues={5}
          legends={[legendConfig]}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
          tooltip={({ id, value, color, indexValue }) => (
            <Box
              sx={{
                background: 'white',
                padding: '9px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: isMobile ? '0.8rem' : '1rem'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    backgroundColor: color,
                    borderRadius: '2px'
                  }} 
                />
                <strong>{id}</strong>
              </Box>
              <div>{indexValue}: {formatValue(value)}</div>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default BarChart; 